import { forwardRef, type HTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

const VARIANTS = {
  default: 'border-border bg-card text-card-foreground',
  warning: 'border-amber-500/40 bg-amber-500/10 text-foreground',
  destructive: 'border-destructive/40 bg-destructive/10 text-foreground',
} as const;

export type AlertVariant = keyof typeof VARIANTS;

export interface AlertProps extends HTMLAttributes<HTMLDivElement> {
  variant?: AlertVariant;
}

export const Alert = forwardRef<HTMLDivElement, AlertProps>(function Alert(
  { className, variant = 'default', ...props },
  ref,
) {
  return (
    <div
      ref={ref}
      role="alert"
      className={cn('flex gap-3 rounded-md border p-4', VARIANTS[variant], className)}
      {...props}
    />
  );
});

export const AlertTitle = forwardRef<HTMLHeadingElement, HTMLAttributes<HTMLHeadingElement>>(
  function AlertTitle({ className, ...props }, ref) {
    return (
      <h3
        ref={ref}
        className={cn('text-sm font-semibold leading-tight tracking-tight', className)}
        {...props}
      />
    );
  },
);

export const AlertDescription = forwardRef<
  HTMLParagraphElement,
  HTMLAttributes<HTMLParagraphElement>
>(function AlertDescription({ className, ...props }, ref) {
  return <div ref={ref} className={cn('text-sm text-muted-foreground', className)} {...props} />;
});
